import React, { useEffect, useRef } from "react";
import {
  Grid,
  TextField,
  Typography,
  Box,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import config from "../../config";

const useLoadGoogleMapsScript = (callback) => {
  useEffect(() => {
    if (!document.querySelector(`[src*="maps.googleapis.com"]`)) {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${config.mapApiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => callback();
      document.head.appendChild(script);
    } else {
      callback();
    }
  }, [callback]);
};

const KitchenDetails = ({
  editMode,
  kitchenData,
  updateKitchenData,
  openTermsModal,
}) => {
  const cityInputRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleChange = (field, value) => {
    updateKitchenData({ [field]: value });
  };

  useLoadGoogleMapsScript(() => {
    if (window.google && window.google.maps && cityInputRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(
        cityInputRef.current,
        { types: ["(cities)"], componentRestrictions: { country: "us" } }
      );
      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (place && place.formatted_address) {
          handleChange("city", place.formatted_address);
        }
      });
    }
  });

  return (
    <Box
      sx={{
        p: isMobile ? 1 : 4,
        background: "#fff",
      }}
    >
      <Typography variant="h6" gutterBottom>
        Kitchen Details
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Kitchen Title"
            value={kitchenData.title || ""}
            onChange={(e) => handleChange("title", e.target.value)}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Kitchen Type</InputLabel>
            <Select
              value={kitchenData.category || ""}
              onChange={(e) => handleChange("category", e.target.value)}
              label="Kitchen Type"
            >
              <MenuItem value="">Select Category</MenuItem>
              <MenuItem value="commercial">Commercial Kitchen</MenuItem>
              <MenuItem value="restaurant">Restaurant Kitchen</MenuItem>
              <MenuItem value="ghost">Ghost Kitchen</MenuItem>
              <MenuItem value="bakery">Bakery Kitchen</MenuItem>
              <MenuItem value="private">Private Kitchen</MenuItem>
              <MenuItem value="all">All Purpose Kitchen</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            inputRef={cityInputRef}
            label="City"
            value={kitchenData.city || ""}
            onChange={(e) => handleChange("city", e.target.value)}
            placeholder="Enter your city"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Kitchen Size</InputLabel>
            <Select
              value={kitchenData.size || ""}
              onChange={(e) => handleChange("size", e.target.value)}
              label="Kitchen Size"
            >
              <MenuItem value="">Select Size</MenuItem>
              <MenuItem value="small">Small</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="large">Large</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Kitchen Description"
            value={kitchenData.description || ""}
            onChange={(e) => handleChange("description", e.target.value)}
            inputProps={{ maxLength: 500 }}
            helperText="This message will appear below your kitchen listing."
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={6}
            label="Check-in Message"
            value={kitchenData.checkinmessage || ""}
            onChange={(e) => handleChange("checkinmessage", e.target.value)}
            inputProps={{ maxLength: 500 }}
            helperText="Shown only after booking confirmation. Do not include personal info. Add key/code instructions."
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={kitchenData.agreed || false}
                onChange={(e) => handleChange("agreed", e.target.checked)}
              />
            }
            label={
              <Typography
                variant="body2"
                component="span"
                onClick={(e) => {
                  e.preventDefault();
                  openTermsModal();
                }}
                sx={{
                  cursor: "pointer",
                  color: "primary.main",
                  textDecoration: "underline",
                }}
              >
                I agree to the Terms and Conditions
              </Typography>
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default KitchenDetails;
